type RegionCode = string;
type AppPath = string;

/** Path is reachable only in the listed regions. */
const REGION_ONLY_PATHS = {
  '/why-ultrahuman': ['CA'],
  '/hsa-fsa': ['US', 'PR'],
  // M2 Live terms cover U.S. purchases only.
  '/termsOfSale/UltrahumanM2': ['US', 'PR'],
} as const satisfies Record<AppPath, readonly RegionCode[]>;

/** Path is reachable everywhere except the listed regions. */
const REGION_EXCLUDED_PATHS = {
  // Keep in step with CLUE_UNSUPPORTED_STORES in productData/addons/clue.ts.
  // US/PR: the bundle is Ring AIR based and AIR is not sold there. JP: the
  // Clue product is not carried by the JP store. Live 404s all three.
  '/ultrahuman-x-clue': ['US', 'PR', 'JP'],
  // '/blood-vision': ['CA'],
} as const satisfies Record<AppPath, readonly RegionCode[]>;

function normalizePath(path: string): AppPath {
  if (!path) return '/';
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  if (withSlash.length > 1 && withSlash.endsWith('/')) {
    return withSlash.slice(0, -1);
  }
  return withSlash;
}

function toPathRegionMap(
  input: Record<AppPath, readonly RegionCode[]>,
): ReadonlyMap<AppPath, ReadonlySet<RegionCode>> {
  return new Map(
    Object.entries(input).map(([path, regions]) => [
      normalizePath(path),
      new Set(regions.map((r) => r.toUpperCase())),
    ]),
  );
}

const regionOnlyPaths = toPathRegionMap(REGION_ONLY_PATHS);
const regionExcludedPaths = toPathRegionMap(REGION_EXCLUDED_PATHS);

/** Returns whether `path` is allowed for `region` (exact path, after region strip). */
export function isPathAllowedForRegion(
  path: AppPath,
  region: RegionCode,
): boolean {
  const key = normalizePath(path);
  const r = region.toUpperCase();

  const only = regionOnlyPaths.get(key);
  if (only) return only.has(r);

  const excluded = regionExcludedPaths.get(key);
  if (excluded) return !excluded.has(r);

  return true;
}
